import Link from "next/link";
import Image from "next/image";
import { Mail } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { footerColumns } from "@/content/nav";
import { company, siteUrl } from "@/content/company";
import { InstagramIcon, LinkedinIcon, WhatsAppIcon } from "@/components/ui/SocialIcons";
import { OpenSignupButton } from "@/components/popups/OpenSignupButton";
import { assetUrl } from "@/lib/asset-url";

export function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden border-t border-line bg-ink-black">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 select-none whitespace-nowrap text-center font-black uppercase italic leading-none"
        style={{ fontSize: "clamp(5rem, 16vw, 220px)", letterSpacing: "0.08em" }}
      >
        {/* base fixa, bem apagada — e por cima, o mesmo texto com uma luz
            (gradiente) passando por dentro das letras via bg-clip: text,
            deslizando em loop. */}
        <p className="text-text-primary/[0.04]">SAT LABS</p>
        <p className="footer-wordmark-shine absolute inset-0">SAT LABS</p>
      </div>

      <Container className="relative py-16">
        <div className="flex flex-col gap-10 border-b border-line pb-12 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-md">
            <Image src={assetUrl("/brand/sat-labs-logo-white.png")} alt="SAT Labs" width={600} height={195} className="h-8 w-auto" />
            <p className="mt-3 text-sm text-text-secondary">
              Infraestrutura de conteúdo para provedores: cloud, CDN, media server, IPTV, OTT,
              VOD, monitoramento e governança de TI em uma única operação.
            </p>
          </div>
          <OpenSignupButton size="lg">Falar com um especialista</OpenSignupButton>
        </div>

        <div className="grid grid-cols-2 gap-10 pt-12 sm:grid-cols-4">
          {footerColumns.map((col) => (
            <div key={col.title}>
              <h3 className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.1em] text-text-muted">
                {col.title}
              </h3>
              <ul className="space-y-2.5">
                {col.links.map((link) =>
                  "external" in link && link.external ? (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-text-secondary transition-colors hover:text-text-primary"
                      >
                        {link.label}
                      </a>
                    </li>
                  ) : (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        className="text-sm text-text-secondary transition-colors hover:text-text-primary"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ),
                )}
              </ul>
            </div>
          ))}

          <div>
            <h3 className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.1em] text-text-muted">
              Contato
            </h3>
            <ul className="space-y-2.5 text-sm text-text-secondary">
              <li>
                <a href={`mailto:${company.email}`} className="flex items-center gap-2 hover:text-text-primary">
                  <Mail className="h-4 w-4 shrink-0" aria-hidden /> {company.email}
                </a>
              </li>
              <li>
                <a
                  href={company.whatsapp.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-text-primary"
                >
                  <WhatsAppIcon className="h-4 w-4 shrink-0" /> {company.whatsapp.display}
                </a>
              </li>
            </ul>
            <div className="mt-4 flex items-center gap-4">
              <a
                href={company.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram da SAT Labs"
                className="text-text-secondary hover:text-text-primary"
              >
                <InstagramIcon />
              </a>
              <a
                href={company.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn da SAT Labs"
                className="text-text-secondary hover:text-text-primary"
              >
                <LinkedinIcon />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-line pt-6 text-sm text-text-secondary sm:flex-row sm:items-center sm:justify-between">
          <p>&copy; {year} SAT Labs. Todos os direitos reservados.</p>
          <div className="flex flex-col gap-2 sm:items-end">
            <p>{siteUrl.replace("https://", "")}</p>
            <p>
              Site desenvolvido por{" "}
              <a
                href="https://www.kblo.com.br"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-text-primary transition-colors hover:text-accent-lime"
              >
                KBLO
              </a>
            </p>
          </div>
        </div>
      </Container>
    </footer>
  );
}
